import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { InvoiceNumberLock } from "./InvoiceNumberLock";

export default function VoucherNumbering() {
  const [rows, setRows] = React.useState([]);
  const [previews, setPreviews] = React.useState({});
  const load = React.useCallback(async () => {
    setRows((await api.get("/settings/voucher-numbering")).data);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const setField = (doc_type, patch) => setRows((rs) => rs.map((r) => r.doc_type === doc_type ? { ...r, ...patch } : r));
  const save = async (r) => {
    try {
      await api.post(`/settings/voucher-numbering/${r.doc_type}`, {
        prefix: r.prefix, format: r.format, reset_policy: r.reset_policy, starting_number: r.starting_number,
      });
      toast.success(`Saved ${r.doc_type}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const preview = async (r) => {
    const res = await api.get("/settings/voucher-numbering/preview", { params: { fmt: r.format, prefix: r.prefix, seq: r.starting_number, branch_code: "MUM" } });
    setPreviews((p) => ({ ...p, [r.doc_type]: res.data.preview }));
  };

  return (
    <div className="card-sharp p-5 space-y-3" data-testid="voucher-numbering-panel">
      <InvoiceNumberLock />
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Configurable series per document type · tokens: {"{prefix}"} {"{branch}"} {"{fy}"} {"{yy}"} {"{yyyy}"} {"{seq:5}"}</div>
      <table className="w-full dense">
        <thead><tr><th>Doc type</th><th>Prefix</th><th>Format</th><th>Reset</th><th className="text-right">Start #</th><th>Preview</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.doc_type} data-testid={`vn-row-${r.doc_type}`}>
              <td className="text-xs font-mono uppercase">{r.doc_type}</td>
              <td className="p-1"><Input value={r.prefix} onChange={(e) => setField(r.doc_type, { prefix: e.target.value.toUpperCase() })} className="rounded-sm h-8 font-mono" data-testid={`vn-prefix-${r.doc_type}`} /></td>
              <td className="p-1"><Input value={r.format} onChange={(e) => setField(r.doc_type, { format: e.target.value })} className="rounded-sm h-8 font-mono text-xs" data-testid={`vn-format-${r.doc_type}`} /></td>
              <td className="p-1">
                <Select value={r.reset_policy} onValueChange={(v) => setField(r.doc_type, { reset_policy: v })}>
                  <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never</SelectItem>
                    <SelectItem value="fiscal_year">Fiscal year</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="p-1"><Input type="number" value={r.starting_number} onChange={(e) => setField(r.doc_type, { starting_number: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
              <td className="text-xs font-mono text-accent" data-testid={`vn-preview-${r.doc_type}`}>{previews[r.doc_type] || "—"}</td>
              <td className="text-right space-x-1">
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => preview(r)}>Preview</Button>
                <Button size="sm" className="h-7 text-xs bg-accent hover:bg-accent/90 text-white" onClick={() => save(r)} data-testid={`vn-save-${r.doc_type}`}>Save</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
